import { useEffect, useMemo, useState } from "react";
import type { Node } from "@xyflow/react";
import type { OperationCanvasNodeData } from "../OperationCanvasNode.js";
import { apiFetch } from "../../lib/api.js";
import {
  loadPlatformEntityCatalog,
  platformEntityViews,
  platformEntityView,
  platformEntityCatalogLoaded,
  type PlatformParamSpec,
} from "../../lib/platformEntityCatalog.js";
import { MemberPicker, type MemberCandidate } from "../MemberPicker.js";
import { NodeOutputStats, type NodeOutputRecord } from "./nodeInspectorWidgets.js";
import { TanTableViz } from "../TanTableViz.js";
import { queryResultsToTanTableModel } from "../../lib/queryResultsToTanTable.js";
import { InspectorTabs } from "../inspector/InspectorTabs.js";
import type { QueryResultsPayload } from "../QueryResultsGrid.js";

/**
 * ADR-0172 — Atlas Provider inspector. Entities, params, defaults, and the
 * column preview all DERIVE from the hub's served entity-view catalog
 * (lib/platformEntityCatalog.ts — the G10 reader); this file names no entity
 * and no vocabulary value of its own.
 */

function humanizeParamName(name: string): string {
  const label = name.replace(/_/g, " ");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function OperationInspectorPlatformProvider({
  selectedNode,
  setNodeData,
  nodeOutputRecord,
  onRunNode,
  runBusy,
  operationStatus,
  cachedPayload,
  cacheLoading,
}: {
  selectedNode: Node<OperationCanvasNodeData>;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  nodeOutputRecord?: NodeOutputRecord;
  onRunNode: (nodeId: string) => void | Promise<void>;
  runBusy: boolean;
  operationStatus: string;
  cachedPayload: QueryResultsPayload | null | undefined;
  cacheLoading: boolean;
}) {
  const [catalogReady, setCatalogReady] = useState(platformEntityCatalogLoaded());
  const [candidates, setCandidates] = useState<MemberCandidate[]>([]);
  const [intDrafts, setIntDrafts] = useState<Record<string, string>>({});
  const [intErrors, setIntErrors] = useState<Record<string, string>>({});

  const config = selectedNode.data.config ?? {};
  const spec = (config.spec_platform_provider as Record<string, unknown> | undefined) ?? {};
  const entityKey = (spec.entity as string | undefined) ?? "";
  const params = (spec.params as Record<string, unknown> | undefined) ?? {};

  useEffect(() => {
    void (async () => {
      await loadPlatformEntityCatalog();
      setCatalogReady(platformEntityCatalogLoaded());
    })();
  }, []);

  const view = platformEntityView(entityKey);
  const needsMembers = useMemo(
    () => Object.values(view?.params ?? {}).some((p) => p.kind === "account_ids" || p.kind === "group_ids"),
    [view]
  );

  useEffect(() => {
    if (!needsMembers) return;
    void (async () => {
      try {
        const data = await apiFetch<{ candidates: MemberCandidate[] }>("/api/tenant/rasci-candidates");
        setCandidates(data.candidates);
      } catch {
        setCandidates([]);
      }
    })();
  }, [needsMembers]);

  function updateSpec(patch: { entity?: string; params?: Record<string, unknown> }) {
    setNodeData(selectedNode.id, (data) => ({
      ...data,
      config: {
        ...data.config,
        spec_platform_provider: {
          entity: patch.entity !== undefined ? patch.entity : entityKey,
          params: patch.params !== undefined ? patch.params : params,
        },
      },
    }));
  }

  function setParam(name: string, value: unknown) {
    const next = { ...params };
    if (value === undefined) {
      delete next[name];
    } else {
      next[name] = value;
    }
    updateSpec({ params: next });
  }

  function renderParam(name: string, paramSpec: PlatformParamSpec) {
    if (paramSpec.kind === "enum_set") {
      const selected = Array.isArray(params[name]) ? (params[name] as string[]) : [...paramSpec.default];
      return (
        <div className="field" key={name}>
          <span className="label">{humanizeParamName(name)}</span>
          <div style={{ display: "grid", gap: 4 }}>
            {paramSpec.values.map((v) => (
              <label key={v} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={selected.includes(v)}
                  onChange={(e) => {
                    const next = e.target.checked ? [...selected, v] : selected.filter((s) => s !== v);
                    // An explicit selection always lands in config (the hub
                    // refuses an empty set with a readable finding).
                    setParam(name, next);
                  }}
                />
                {v.replace(/_/g, " ")}
              </label>
            ))}
          </div>
        </div>
      );
    }
    if (paramSpec.kind === "account_ids" || paramSpec.kind === "group_ids") {
      const selected = Array.isArray(params[name]) ? (params[name] as string[]) : [];
      return (
        <div className="field" key={name}>
          <span className="label">{humanizeParamName(name)}</span>
          <MemberPicker
            mode="multi"
            allowedKinds={[paramSpec.kind === "account_ids" ? "user" : "group"]}
            candidates={candidates}
            selected={selected}
            onChange={(next) => setParam(name, next.length > 0 ? next : undefined)}
            placeholder="Everyone (no filter)"
          />
          <span className="help">Leave empty for no filter</span>
        </div>
      );
    }
    // int
    const current = typeof params[name] === "number" ? String(params[name]) : "";
    const draft = intDrafts[name] ?? current;
    return (
      <div className="field" key={name}>
        <label className="label">{humanizeParamName(name)}</label>
        <input
          className="input mono"
          type="number"
          min={paramSpec.min}
          max={paramSpec.max}
          placeholder={String(paramSpec.default)}
          value={draft}
          onChange={(e) => setIntDrafts((d) => ({ ...d, [name]: e.target.value }))}
          onBlur={() => {
            const raw = (intDrafts[name] ?? "").trim();
            if (raw === "") {
              setIntErrors((er) => ({ ...er, [name]: "" }));
              setParam(name, undefined);
              return;
            }
            const n = Number(raw);
            if (!Number.isInteger(n) || n < paramSpec.min || n > paramSpec.max) {
              setIntErrors((er) => ({
                ...er,
                [name]: `Must be a whole number between ${paramSpec.min} and ${paramSpec.max}`,
              }));
              return;
            }
            setIntErrors((er) => ({ ...er, [name]: "" }));
            setParam(name, n);
          }}
        />
        <span className="help">
          {intErrors[name] ? intErrors[name] : `Default ${paramSpec.default}, up to ${paramSpec.max}`}
        </span>
      </div>
    );
  }

  const cachedTanModel = useMemo(
    () => (cachedPayload ? queryResultsToTanTableModel(cachedPayload) : null),
    [cachedPayload]
  );

  const views = platformEntityViews();

  return (
    <InspectorTabs
      surfaceId="operationEditor"
      tabGroupId="platform_provider"
      tabs={[
        {
          slot: "setup",
          content: (
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 12 }}>
              <div className="field">
                <label className="label">Entity</label>
                <select
                  className="select"
                  value={entityKey}
                  onChange={(e) => {
                    // Switching entity re-points the whole output — params
                    // reset so a stale filter can never survive the switch.
                    setIntDrafts({});
                    setIntErrors({});
                    updateSpec({ entity: e.target.value, params: {} });
                  }}
                >
                  <option value="">— Select what to serve —</option>
                  {views.map((v) => (
                    <option key={v.key} value={v.key}>
                      {v.label}
                    </option>
                  ))}
                </select>
                {!catalogReady ? (
                  <span className="help" style={{ color: "var(--danger-text)" }}>
                    The entity catalog could not be loaded — reload the page to retry.
                  </span>
                ) : view ? (
                  <span className="help">{view.description}</span>
                ) : null}
              </div>

              {view ? Object.entries(view.params).map(([name, p]) => renderParam(name, p)) : null}

              {view ? (
                <div className="field">
                  <span className="label">Columns</span>
                  <div className="tableWrap">
                    <table className="table tableCompact">
                      <thead>
                        <tr>
                          <th>Column</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {view.columns.map((c) => (
                          <tr key={c.name}>
                            <td className="mono">{c.name}</td>
                            <td>{c.type}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <span className="help">Declared by the platform — this is the step&apos;s exact output schema</span>
                </div>
              ) : null}
            </div>
          ),
        },
        {
          slot: "data",
          content: (
            <div className="card" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <strong>
                  Cached data
                  {cachedPayload?.rowCount != null ? ` — ${cachedPayload.rowCount.toLocaleString()} rows` : ""}
                </strong>
                <button
                  className="btn btnSm"
                  type="button"
                  disabled={runBusy || operationStatus === "validate" || !entityKey}
                  onClick={() => void onRunNode(selectedNode.id)}
                  title={
                    operationStatus === "validate"
                      ? "Operation requires validation"
                      : !entityKey
                        ? "Select an entity first"
                        : "Read platform data and refresh this node's cache"
                  }
                >
                  {runBusy ? "Running…" : "Run this node"}
                </button>
              </div>
              <NodeOutputStats rec={nodeOutputRecord} />
              {cacheLoading ? (
                <div className="muted">Loading cache…</div>
              ) : cachedTanModel === null ? (
                <div className="muted">No cached data. Click &quot;Run this node&quot; to read platform data and populate the cache.</div>
              ) : (
                <div className="inspectorTanTableHost">
                  <TanTableViz model={cachedTanModel} title={view?.label} />
                </div>
              )}
            </div>
          ),
        },
      ]}
    />
  );
}
